import { Box, IconButton } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import zeplinColor from '../../../../theme/Color';
import { WidgetSize } from '../../../../model/widget.model';

// ─── Grid-position types (backend-friendly) ─────────────────────────────────

export type GridPosition = { row: number; col: number };

export type ColumnConfig = {
  /** Unique key stored in the backend, e.g. 'activity', 'tasks', 'meetings' */
  id: string;
  /** The React element to render (already has its own props wired up) */
  render: () => React.ReactNode;
  /** Current [row, col] position — comes from the backend / state */
  position: GridPosition;
  /** Optional column span, e.g. 2 for DoubleColumnComponent */
  colSpan?: number;
  /** Whether the column can be removed (shows cancel-square button) */
  removable?: boolean;
};

type HomepageOverviewTabProps = {
  /** Array of all column cards to display in the grid */
  columns: ColumnConfig[];
  /** Called after two columns swap – send the updated positions to the backend */
  onPositionChange?: (positions: Record<string, GridPosition>) => void;
  /** Called when the user clicks the remove (✕) button on a column */
  onRemoveWidget?: (widgetId: string) => void;
  /** Called when a column is resized – sends widgetId and new size */
  onResizeWidget?: (widgetId: string, size: WidgetSize, newSpan: number) => void;
  /** Total number of grid columns (default 3) */
  gridCols?: number;
};

// ─── Span ↔ WidgetSize helpers ───────────────────────────────────────────────

const spanToSize = (span: number): WidgetSize => {
  if (span >= 3) return 'large';
  if (span === 2) return 'medium';
  return 'small';
};

// ─── Local-storage helpers ───────────────────────────────────────────────────

const STORAGE_KEY = 'homepage-column-positions';

const readPositions = (columns: ColumnConfig[]): Record<string, GridPosition> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, GridPosition>;
      const ids = columns.map((c) => c.id).sort();
      const parsedIds = Object.keys(parsed).sort();
      if (JSON.stringify(ids) === JSON.stringify(parsedIds)) return parsed;
    }
  } catch {
    /* ignore */
  }
  return Object.fromEntries(columns.map((c) => [c.id, c.position]));
};

const savePositions = (positions: Record<string, GridPosition>) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    /* ignore */
  }
};

// ─── Main Component ──────────────────────────────────────────────────────────

const HomepageOverviewTab: React.FC<HomepageOverviewTabProps> = ({
  columns,
  onPositionChange,
  onRemoveWidget,
  onResizeWidget,
  gridCols = 3,
}) => {
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // ─── Resize state ─────────────────────────────────────────────────────────
  const [colSpans, setColSpans] = useState<Record<string, number>>(() =>
    Object.fromEntries(columns.map((c) => [c.id, c.colSpan ?? 1])),
  );
  const resizeRef = useRef<{
    columnId: string;
    startX: number;
    startSpan: number;
    startWidthPx: number;
    currentWidthPx: number;
    cellWidth: number;
    gapPx: number;
  } | null>(null);

  // Re-sync colSpans when columns prop changes
  useEffect(() => {
    setColSpans((prev) => {
      const next: Record<string, number> = {};
      for (const c of columns) {
        next[c.id] = prev[c.id] ?? c.colSpan ?? 1;
      }
      return next;
    });
  }, [columns]);

  // Tracks whether the drag was initiated from the move handle
  const dragHandleActive = useRef(false);
  useEffect(() => {
    const reset = () => {
      dragHandleActive.current = false;
    };
    window.addEventListener('mouseup', reset);
    return () => window.removeEventListener('mouseup', reset);
  }, []);

  // Positions – seeded from props (API), persisted locally as cache ────────

  const [positions, setPositions] = useState<Record<string, GridPosition>>(() =>
    readPositions(columns),
  );

  // Re-sync positions when columns change (e.g. new widget added via API)
  React.useEffect(() => {
    setPositions(readPositions(columns));
  }, [columns]);

  // ColSpan lookup: id → span (uses resize state)
  const spanMap = useMemo(() => ({ ...colSpans }), [colSpans]);

  // Compute the number of grid rows needed
  const maxRow = Math.max(...Object.values(positions).map((p) => p.row), 1);

  // ─── Resize handlers ─────────────────────────────────────────────────────

  const gridRef = useRef<HTMLDivElement>(null);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.preventDefault();
      e.stopPropagation();

      const gridEl = gridRef.current;
      if (!gridEl) return;

      const gridWidth = gridEl.getBoundingClientRect().width;
      const style = window.getComputedStyle(gridEl);
      const gapPx = parseFloat(style.columnGap) || 32;
      const cellWidth = (gridWidth - gapPx * (gridCols - 1)) / gridCols;

      const startSpan = colSpans[columnId] ?? 1;
      const startWidthPx = startSpan * cellWidth + (startSpan - 1) * gapPx;

      resizeRef.current = {
        columnId,
        startX: e.clientX,
        startSpan,
        startWidthPx,
        currentWidthPx: startWidthPx,
        cellWidth,
        gapPx,
      };

      const handleMouseMove = (ev: MouseEvent) => {
        if (!resizeRef.current) return;
        const deltaX = ev.clientX - resizeRef.current.startX;
        const minW = resizeRef.current.cellWidth;
        const maxW =
          gridCols * resizeRef.current.cellWidth + (gridCols - 1) * resizeRef.current.gapPx;
        const newWidth = Math.max(minW, Math.min(maxW, resizeRef.current.startWidthPx + deltaX));
        // Store live pixel width in ref (always fresh for mouseup)
        resizeRef.current.currentWidthPx = newWidth;

        // Update grid span live so grid layout stays valid
        const { cellWidth: cw, gapPx: g } = resizeRef.current;
        const rawSpan = (newWidth + g) / (cw + g);
        const liveSpan = Math.max(1, Math.min(gridCols, Math.round(rawSpan)));
        setColSpans((prev) => {
          if (prev[columnId] === liveSpan) return prev;
          return { ...prev, [columnId]: liveSpan };
        });
      };

      const handleMouseUp = () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);

        const resizeData = resizeRef.current;
        resizeRef.current = null;
        if (!resizeData) return;

        // Read the final pixel width from the ref (always up-to-date)
        const { cellWidth: cw, gapPx: g, startSpan: origSpan, currentWidthPx } = resizeData;
        const rawSpan = (currentWidthPx + g) / (cw + g);
        const snappedSpan = Math.max(1, Math.min(gridCols, Math.round(rawSpan)));

        // If threshold wasn't crossed, revert to original span
        if (snappedSpan === origSpan) {
          setColSpans((prev) => ({ ...prev, [resizeData.columnId]: origSpan }));
          return;
        }

        // Commit the new span
        setColSpans((prev) => {
          const next = { ...prev, [resizeData.columnId]: snappedSpan };
          const size = spanToSize(snappedSpan);
          onResizeWidget?.(resizeData.columnId, size, snappedSpan);

          // Reflow: push displaced neighbours to next row
          const id = resizeData.columnId;
          const pos = positions[id];
          if (pos && snappedSpan > 1) {
            setPositions((prevPos) => {
              const nextPos = { ...prevPos };
              const maxExistingRow = Math.max(...Object.values(prevPos).map((p) => p.row), 1);
              let overflow = maxExistingRow + 1;

              for (const [otherId, otherPos] of Object.entries(prevPos)) {
                if (otherId === id) continue;
                if (otherPos.row === pos.row) {
                  const otherSpan = next[otherId] ?? 1;
                  const myEnd = pos.col + snappedSpan - 1;
                  const otherEnd = otherPos.col + otherSpan - 1;
                  const overlaps = pos.col <= otherEnd && otherPos.col <= myEnd;
                  if (overlaps) {
                    nextPos[otherId] = { row: overflow, col: otherPos.col };
                    overflow++;
                  }
                }
              }

              savePositions(nextPos);
              onPositionChange?.(nextPos);
              return nextPos;
            });
          }

          return next;
        });
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [colSpans, gridCols, onResizeWidget, positions, onPositionChange],
  );

  // ─── Native HTML5 DnD handlers (same pattern as LeadsKanban) ──────────────

  const handleOnDragStart = useCallback((e: React.DragEvent, columnId: string) => {
    e.dataTransfer.setData('columnId', columnId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(columnId);
  }, []);

  const handleOnDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const target = (e.target as HTMLElement).closest('[data-column-id]');

    if (target) {
      setDragOverId((target as HTMLElement).dataset.columnId || null);
    }
  }, []);

  const handleOnDrop = useCallback(
    (e: React.DragEvent, targetId: string) => {
      e.preventDefault();
      setDragOverId(null);

      const sourceId = e.dataTransfer.getData('columnId');
      console.log('Dropped', { sourceId, targetId });
      if (!sourceId || sourceId === targetId) return;

      setPositions((prev) => {
        const sourcePos = prev[sourceId];
        const targetPos = prev[targetId];
        if (!sourcePos || !targetPos) return prev;

        const sourceSpan = spanMap[sourceId] ?? 1;
        const targetSpan = spanMap[targetId] ?? 1;

        // ── Simple swap: both single-span ──────────────────────
        if (sourceSpan === 1 && targetSpan === 1) {
          const next = { ...prev, [sourceId]: targetPos, [targetId]: sourcePos };
          savePositions(next);
          onPositionChange?.(next);
          return next;
        }

        // ── Same row with a wide component: no-op (wide can't move within row) ──
        if (sourcePos.row === targetPos.row) return prev;

        // ── Cross-row swap involving a wide (colSpan > 1) component ──
        const next = { ...prev };

        const wideId = sourceSpan > 1 ? sourceId : targetId;
        const singleId = sourceSpan > 1 ? targetId : sourceId;
        const widePos = prev[wideId];
        const singlePos = prev[singleId];
        const wideSpan = spanMap[wideId] ?? 1;

        // Wide component always lands at col 1 in the single item's row
        next[wideId] = { row: singlePos.row, col: 1 };

        // Single item takes the wide component's old row & col
        next[singleId] = { row: widePos.row, col: widePos.col };

        // Columns occupied by the wide component in its new position
        const wideOccupies = new Set<number>();
        for (let c = 1; c <= wideSpan; c++) wideOccupies.add(c);

        // Find collateral: items in the single item's old row that now
        // sit underneath the wide component (excluding source & target)
        const collateral: string[] = [];
        for (const [id, pos] of Object.entries(prev)) {
          if (id === sourceId || id === targetId) continue;
          if (pos.row === singlePos.row && wideOccupies.has(pos.col)) {
            collateral.push(id);
          }
        }

        // Move collateral items to the wide component's old row
        // First, figure out which cols are already taken there
        const usedInOldRow = new Set<number>();
        const singleNewSpan = spanMap[singleId] ?? 1;
        for (let c = next[singleId].col; c < next[singleId].col + singleNewSpan; c++) {
          usedInOldRow.add(c);
        }
        for (const [id, pos] of Object.entries(prev)) {
          if (id === sourceId || id === targetId) continue;
          if (collateral.includes(id)) continue;
          if (pos.row === widePos.row) usedInOldRow.add(pos.col);
        }

        const maxExistingRow = Math.max(...Object.values(prev).map((p) => p.row), 1);
        let nextNewRow = maxExistingRow + 1;

        for (const id of collateral) {
          let placed = false;
          for (let c = 1; c <= gridCols; c++) {
            if (!usedInOldRow.has(c)) {
              next[id] = { row: widePos.row, col: c };
              usedInOldRow.add(c);
              placed = true;
              break;
            }
          }
          if (!placed) {
            next[id] = { row: nextNewRow, col: prev[id].col };
            nextNewRow++;
          }
        }

        savePositions(next);
        onPositionChange?.(next);
        return next;
      });
    },
    [onPositionChange, spanMap, gridCols],
  );

  const handleDragEnd = useCallback(() => {
    setDragOverId(null);
    setDraggingId(null);
  }, []);

  // Drop-zone props (applied to each column Box — NOT draggable itself) ──────

  const getDropZoneProps = useCallback(
    (columnId: string) => ({
      'data-column-id': columnId,
      onDragOver: handleOnDragOver,
      onDrop: (e: React.DragEvent) => handleOnDrop(e, columnId),
      onDragEnd: handleDragEnd,
    }),
    [handleOnDragOver, handleOnDrop, handleDragEnd],
  );

  // Drag-over outline (uses outline so it doesn't affect layout) ─────────────

  const dragOverSx = useCallback(
    (columnId: string) =>
      dragOverId === columnId
        ? {
            outline: `2px dashed ${zeplinColor.Brand1 ?? '#1976d2'}`,
            outlineOffset: '-2px',
            borderRadius: '12px',
            opacity: 0.7,
          }
        : draggingId === columnId
          ? { opacity: 0.5 }
          : {},
    [dragOverId, draggingId],
  );

  // ─── Sort columns by their position (row, col) for auto-flow ──────────────

  const sortedColumns = useMemo(() => {
    return [...columns].sort((a, b) => {
      const posA = positions[a.id] ?? a.position;
      const posB = positions[b.id] ?? b.position;
      if (posA.row !== posB.row) return posA.row - posB.row;
      return posA.col - posB.col;
    });
  }, [columns, positions]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box
      ref={gridRef}
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
        gridAutoRows: 'auto',
        gridAutoFlow: 'row dense',
        columnGap: { xs: '20px', xl: '32px' },
        rowGap: '24px',
        width: '100%',
        flex: 1,
        overflowY: 'auto',
        maxHeight: { xs: 'calc(100vh - 340px)', xl: 'calc(100vh - 380px)' },
        paddingBottom: '24px',
      }}
    >
      {sortedColumns.map((col) => {
        const span = colSpans[col.id] ?? col.colSpan ?? 1;

        return (
          <Box
            key={col.id}
            draggable
            {...getDropZoneProps(col.id)}
            onDragStart={(e: React.DragEvent) => {
              // Only allow drag if mousedown started on the move handle
              if (!dragHandleActive.current) {
                e.preventDefault();
                return;
              }
              dragHandleActive.current = false;
              handleOnDragStart(e, col.id);
            }}
            onMouseEnter={() => setHoveredId(col.id)}
            onMouseLeave={() => setHoveredId(null)}
            sx={{
              position: 'relative',
              gridColumn: `span ${span}`,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              minHeight: 0,
              // Make the rendered column fill the entire grid cell height
              '& > *:first-of-type': { flex: 1 },
              // Hover highlight with black border
              border:
                hoveredId === col.id
                  ? `2px solid ${zeplinColor.BasicBlack}`
                  : '2px solid transparent',
              borderRadius: '14px',
              transition: 'border-color 0.15s ease',
              ...dragOverSx(col.id),
              // Show action bar on hover
              '&:hover .column-action-bar': {
                opacity: 1,
                pointerEvents: 'auto',
              },
              '&:hover .resize-handle': {
                opacity: 1,
                pointerEvents: 'auto',
              },
            }}
          >
            {col.render()}

            {/* ── Right-edge resize handle ─────────────────────── */}
            {
              <Box
                className='resize-handle'
                onMouseDown={(e) => handleResizeStart(e, col.id)}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: '-1px',
                  transform: 'translateY(-50%) translateX(50%)',
                  width: '24px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'col-resize',
                  opacity: 0,
                  pointerEvents: 'none',
                  transition: 'opacity 0.15s ease',
                  zIndex: 1000,
                }}
              ></Box>
            }

            {/* ── Hover action bar (bottom-right) ─────────────────── */}
            <Box
              className='column-action-bar'
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px',
                borderRadius: '80px',
                backdropFilter: 'blur(10px)',
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.2s ease',
                zIndex: 999,
              }}
            >
              {/* Move handle */}
              <IconButton
                data-drag-handle
                sx={{
                  border: `1px solid ${zeplinColor.Neutral6}`,
                  padding: '10px',
                  borderRadius: '50px',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                }}
                onMouseDown={() => {
                  dragHandleActive.current = true;
                }}
              >
                <img src='/assets/move.svg' alt='move' draggable={false} width={22} height={22} />
              </IconButton>

              {/* Remove widget — only for optional (removable) columns */}
              {col.removable && (
                <IconButton
                  onClick={() => onRemoveWidget?.(col.id)}
                  sx={{
                    border: `1px solid ${zeplinColor.Neutral6}`,
                    padding: '10px',
                    borderRadius: '50px',
                  }}
                >
                  <img
                    src='/assets/cancel-square.svg'
                    alt='remove'
                    draggable={false}
                    width={22}
                    height={22}
                  />
                </IconButton>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default HomepageOverviewTab;
